/**
 * The side menu's structure: a few top-level groups with collapsible
 * sub-items.
 *
 * - Every page (the app shell's own pages below and every registered
 *   FeaturePage) belongs to exactly one group: FeaturePage.group, or a default
 *   from its section.
 * - A person sees a page when their role is listed, the page's `visible` gate
 *   passes and their permission switches allow it (pageAllowed). The Admin
 *   group is shown to Parlyst platform admins only.
 * - Badges use fields already in the core snapshot; the menu never makes a
 *   request of its own.
 */
import {
  Building2,
  CalendarDays,
  ChartNoAxesCombined,
  LayoutDashboard,
  MessageCircle,
  NotebookPen,
  Package,
  Plug,
  ScanLine,
  Settings,
  ShieldCheck,
  ShoppingBag,
  Users,
} from "lucide-react";
import type { ComponentType } from "react";
import type { Snapshot } from "../api";
import { featurePages } from "../features";
import type { FeaturePage, NavGroupId } from "../features/types";
import { pageAllowed } from "../features/team/pageAccess";

type Icon = ComponentType<{ size?: number }>;

/** A page as the menu sees it. */
export type NavPage = {
  id: string;
  label: string;
  icon: Icon;
  group: NavGroupId;
  roles: string[];
  visible?: (s: Snapshot) => boolean;
  labelFor?: (s: Snapshot) => string;
  keywords?: string[];
};
export type NavGroup = { id: NavGroupId; label: string; icon: Icon; items: NavPage[] };

const ALL_ROLES = ["owner", "manager", "auditor", "counter"];
const NOT_COUNTER = ["owner", "manager", "auditor"];

/** The app shell's own pages (rendered by App.tsx, not feature modules). */
export const shellPages: NavPage[] = [
  { id: "today", label: "Today", icon: LayoutDashboard, group: "today", roles: ALL_ROLES },
  { id: "counts", label: "Count inventory", icon: ScanLine, group: "count", roles: ALL_ROLES },
  { id: "inventory", label: "Products & stock", icon: Package, group: "inventory", roles: ALL_ROLES },
  { id: "purchasing", label: "Orders & deliveries", icon: ShoppingBag, group: "buying", roles: NOT_COUNTER },
  { id: "recipes", label: "Recipes & batches", icon: NotebookPen, group: "menu", roles: NOT_COUNTER },
  { id: "reports", label: "Reports", icon: ChartNoAxesCombined, group: "reports", roles: NOT_COUNTER },
  { id: "team", label: "People & access", icon: Users, group: "company", roles: NOT_COUNTER },
  { id: "service", label: "Inventory service", icon: CalendarDays, group: "company", roles: NOT_COUNTER },
  { id: "connections", label: "Integrations", icon: Plug, group: "settings", roles: NOT_COUNTER },
  { id: "settings", label: "Workspace settings", icon: Settings, group: "settings", roles: ALL_ROLES },
];
/** Shown for any page id App.tsx does not recognise (it renders the settings page). */
export const FALLBACK_PAGE = "settings";

/** The groups in menu order, each with the preferred order of its pages (others follow). */
export const navGroups: { id: NavGroupId; label: string; icon: Icon; order: string[] }[] = [
  { id: "today", label: "Today", icon: LayoutDashboard, order: ["today"] },
  // Sam is a top-level entry for every role (a one-page group renders as a plain link).
  { id: "assistant", label: "Ask Parlyst", icon: MessageCircle, order: ["assistant"] },
  { id: "count", label: "Count", icon: ScanLine, order: ["counts", "par-review", "print", "scales"] },
  { id: "inventory", label: "Inventory", icon: Package, order: ["inventory", "nc-catalog", "deposits", "cleanup"] },
  { id: "buying", label: "Buying", icon: ShoppingBag, order: ["suppliers", "purchasing", "invoices", "cost-changes", "purchases", "bills", "transfers"] },
  { id: "menu", label: "Menu", icon: NotebookPen, order: ["recipes", "mapping", "costing"] },
  { id: "reports", label: "Reports", icon: ChartNoAxesCombined, order: ["reports", "sales", "history", "accounting", "books"] },
  { id: "company", label: "Company", icon: Building2, order: ["company", "venues", "team", "service", "alerts", "switching"] },
  {
    id: "settings",
    label: "Settings",
    icon: Settings,
    order: ["settings", "connections", "staff-mode", "account", "billing", "ai-access", "cheat-sheet", "help"],
  },
  { id: "admin", label: "Admin", icon: ShieldCheck, order: ["admin-companies", "invites"] },
];
const GROUP_IDS = new Set<string>(navGroups.map((g) => g.id));

/** A feature page's group: its own, or Inventory for daily pages and Settings for business pages. */
export function groupOfPage(page: Pick<FeaturePage, "group" | "section">): NavGroupId {
  return page.group ?? (page.section === "business" ? "settings" : "inventory");
}

/** Every navigable page: the shell's own and each registered feature page. */
export function allPages(features: FeaturePage[] = featurePages): NavPage[] {
  return [
    ...shellPages,
    ...features.map((f) => ({
      id: f.id,
      label: f.label,
      icon: f.icon,
      group: groupOfPage(f),
      roles: f.roles,
      visible: f.visible,
      labelFor: f.labelFor,
      keywords: f.keywords,
    })),
  ];
}

/** Whether this person may open the page from the menu. */
export function canSee(s: Snapshot, page: NavPage): boolean {
  if (!page.roles.includes(s.role)) return false;
  if (page.group === "admin" && !s.platformAdmin) return false;
  if (page.visible && !page.visible(s)) return false;
  return pageAllowed(s, page.id);
}

/** The menu for this person: groups in order, each with the pages they may open (empty groups left out). */
export function buildMenu(s: Snapshot, features: FeaturePage[] = featurePages): NavGroup[] {
  const pages = allPages(features)
    .filter((p) => canSee(s, p))
    .map((p) => (p.labelFor ? { ...p, label: p.labelFor(s) } : p));
  return navGroups
    .map((g) => {
      const rank = (p: NavPage) => {
        const i = g.order.indexOf(p.id);
        return i < 0 ? g.order.length : i;
      };
      const items = pages.filter((p) => p.group === g.id).sort((a, b) => rank(a) - rank(b));
      return { id: g.id, label: g.label, icon: g.icon, items };
    })
    .filter((g) => g.items.length > 0);
}

/** The group holding a page id, if the page is in the menu. */
export function groupOf(menu: NavGroup[], pageId: string): NavGroupId | undefined {
  return menu.find((g) => g.items.some((p) => p.id === pageId))?.id;
}

/**
 * The top-bar title for the page on screen, matching what App.tsx renders: a
 * feature page this person may open, a shell page, else the settings page.
 */
export function pageTitle(s: Snapshot, pageId: string, features: FeaturePage[] = featurePages): string {
  const feature = features.find((f) => f.id === pageId);
  if (feature && feature.roles.includes(s.role) && (!feature.visible || feature.visible(s)) && pageAllowed(s, feature.id)) return feature.labelFor?.(s) ?? feature.label;
  const shell = shellPages.find((p) => p.id === pageId) ?? shellPages.find((p) => p.id === FALLBACK_PAGE)!;
  return shell.label;
}

/** Pending work per page, from the core snapshot only (no requests). */
export function navBadges(s: Snapshot): Record<string, { count: number; text: string }> {
  const out: Record<string, { count: number; text: string }> = {};
  const put = (id: string, count: unknown, text: string) => {
    const n = Number(count) || 0;
    if (n > 0) out[id] = { count: n, text: `${n} ${text}` };
  };
  put("counts", (s.counts ?? []).filter((c) => c.status === "draft" || c.status === "review").length, "in progress");
  put("par-review", Array.isArray(s.stockouts) ? s.stockouts.length : 0, "ran out");
  put("invoices", s.invoiceSummary?.waiting, "waiting");
  put("alerts", s.alerts?.unread, "unread");
  return out;
}

/** Pages for global search: every page in this person's menu, with its group's name. */
export function searchablePages(menu: NavGroup[]): { id: string; label: string; group: string; keywords?: string[] }[] {
  return menu.flatMap((g) => g.items.map((p) => ({ id: p.id, label: p.label, group: g.label, ...(p.keywords ? { keywords: p.keywords } : {}) })));
}

export { matchPages } from "./pageSearch";

/* Which groups each person keeps open (this browser only). */
const storageKey = (userId: string) => `parlyst.nav.open.${userId || "anonymous"}`;
export function loadOpenGroups(userId: string): NavGroupId[] {
  try {
    const value = JSON.parse(localStorage.getItem(storageKey(userId)) ?? "[]");
    return Array.isArray(value) ? value.filter((x): x is NavGroupId => typeof x === "string" && GROUP_IDS.has(x)) : [];
  } catch {
    return [];
  }
}
export function saveOpenGroups(userId: string, groups: NavGroupId[]) {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(groups));
  } catch {
    // Private mode or storage full: the menu still works, it just forgets.
  }
}
